// Sheets for making and changing things, alerts for what can't be undone, and toasts to say something's done. Each is
// the browser's own modal dialog, so focus stays inside it, Escape closes it, and focus goes back where it was.
import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Button } from './Button';
import { cx } from './cx';
import { CheckCircleIcon } from './icons';
import s from './Sheet.module.css';

/**
 * A modal dialog shown while `open`, and closed when it isn't. Once it's open, focus goes to what's marked
 * `data-autofocus` inside it, if anything is (React's own autoFocus runs before the dialog shows).
 */
function useModal(open: boolean) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) {
      return;
    }
    if (open && !dialog.open) {
      dialog.showModal();
      dialog.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);
  return ref;
}

export interface SheetAction {
  label: string;
  /** Submits this form, by its id, rather than doing `onClick`. */
  form?: string;
  onClick?: () => void;
  busy?: boolean;
  disabled?: boolean;
}

/**
 * A sheet: up from the bottom on a phone, a panel in the middle on a desktop. Cancel on the left, the title, and its
 * one action on the right, as iOS has them. Escape or a press outside it cancels, unless it's busy. Mark the field to
 * start in with `data-autofocus`.
 */
export function Sheet({
  open,
  onClose,
  title,
  action,
  cancelLabel = 'Cancel',
  size = 'regular',
  footer,
  children,
  describedBy,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  action?: SheetAction;
  cancelLabel?: string;
  size?: 'regular' | 'large';
  /** Below the body, pinned: further actions. */
  footer?: ReactNode;
  children: ReactNode;
  /** The id of what describes the sheet, if anything. */
  describedBy?: string;
}) {
  const ref = useModal(open);
  const id = useId();
  const busy = action?.busy ?? false;
  return (
    <dialog
      ref={ref}
      className={cx(s.sheet, size === 'large' && s.large)}
      aria-labelledby={`${id}-title`}
      aria-describedby={describedBy}
      onCancel={(e) => {
        e.preventDefault();
        if (!busy) {
          onClose();
        }
      }}
      onPointerDown={(e) => {
        // A press on the scrim, outside the sheet.
        const box = e.currentTarget.getBoundingClientRect();
        const outside = e.clientX < box.left || e.clientX > box.right || e.clientY < box.top || e.clientY > box.bottom;
        if (e.target === e.currentTarget && outside && !busy) {
          onClose();
        }
      }}
    >
      <div className={s.grabber} aria-hidden="true" />
      <div className={s.head}>
        <Button variant="plain" onClick={onClose} disabled={busy}>
          {cancelLabel}
        </Button>
        <h2 id={`${id}-title`} className={s.title}>
          {title}
        </h2>
        {action ? (
          <Button
            variant="plain"
            type={action.form ? 'submit' : 'button'}
            form={action.form}
            onClick={action.form ? undefined : action.onClick}
            busy={busy}
            disabled={action.disabled}
            className={s.action}
          >
            {action.label}
          </Button>
        ) : (
          <span />
        )}
      </div>
      {open && <div className={s.body}>{children}</div>}
      {open && footer && <div className={s.foot}>{footer}</div>}
    </dialog>
  );
}

export interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  /** The action's label: "Delete car". */
  confirm: string;
  cancel?: string;
  /** Red, for what can't be undone. Default true. */
  destructive?: boolean;
}

type Ask = (options: ConfirmOptions) => Promise<boolean>;
type Say = (message: string) => void;

const AlertContext = createContext<Ask | null>(null);
const ToastContext = createContext<Say | null>(null);

/** Alerts and toasts for the whole app: one of each at a time. */
export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [asking, setAsking] = useState<(ConfirmOptions & { answer: (yes: boolean) => void }) | null>(null);
  const [toast, setToast] = useState<{ message: string; key: number } | null>(null);
  const ref = useModal(asking !== null);
  const id = useId();
  const cancel = useRef<HTMLButtonElement>(null);

  const ask = useCallback<Ask>(
    (options) =>
      new Promise<boolean>((resolve) => {
        setAsking({ ...options, answer: resolve });
      }),
    [],
  );
  const say = useCallback<Say>((message) => setToast({ message, key: Date.now() }), []);

  useEffect(() => {
    if (asking) {
      // Cancel is the safe choice, so it has focus.
      cancel.current?.focus();
    }
  }, [asking]);
  useEffect(() => {
    if (!toast) {
      return;
    }
    const t = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(t);
  }, [toast]);

  const answer = (yes: boolean) => {
    asking?.answer(yes);
    setAsking(null);
  };
  const destructive = asking?.destructive ?? true;

  return (
    <AlertContext.Provider value={ask}>
      <ToastContext.Provider value={say}>
        {children}
        <dialog
          ref={ref}
          role="alertdialog"
          className={s.alert}
          aria-labelledby={`${id}-title`}
          aria-describedby={asking?.message ? `${id}-message` : undefined}
          onCancel={(e) => {
            e.preventDefault();
            answer(false);
          }}
        >
          {asking && (
            <>
              <div className={s.alertText}>
                <h2 id={`${id}-title`} className={s.alertTitle}>
                  {asking.title}
                </h2>
                {asking.message && (
                  <div id={`${id}-message`} className={s.alertMessage}>
                    {asking.message}
                  </div>
                )}
              </div>
              <div className={s.alertButtons}>
                <button
                  ref={cancel}
                  type="button"
                  onClick={() => answer(false)}
                  className={destructive ? undefined : s.alertMain}
                >
                  {asking.cancel ?? 'Cancel'}
                </button>
                <button
                  type="button"
                  onClick={() => answer(true)}
                  className={destructive ? s.alertDestructive : s.alertMain}
                >
                  {asking.confirm}
                </button>
              </div>
            </>
          )}
        </dialog>
        <div className={s.toasts} role="status" aria-live="polite">
          {toast && (
            <div key={toast.key} className={s.toast}>
              <CheckCircleIcon size={18} weight="fill" aria-hidden="true" />
              {toast.message}
            </div>
          )}
        </div>
      </ToastContext.Provider>
    </AlertContext.Provider>
  );
}

/** Asks before something that can't be undone: true when the person goes ahead. */
export function useConfirm(): Ask {
  const ask = useContext(AlertContext);
  if (!ask) {
    throw new Error('useConfirm needs a FeedbackProvider');
  }
  return ask;
}

/** Says something's done, briefly. Errors stay on the page instead. */
export function useToast(): Say {
  const say = useContext(ToastContext);
  if (!say) {
    throw new Error('useToast needs a FeedbackProvider');
  }
  return say;
}
